export function calculateAgeFromBirthdate(birthdate: string | Date | null | undefined, today = new Date()) {
  return calculateAgeDetailsFromBirthdate(birthdate, today)?.completedYears ?? null;
}

export type BirthdatePrecision = "exact" | "month-year" | "year-only" | "age-only" | "unknown";

export type BirthdateSource = "manual" | "legacy-import";

export type AgeSource = "date-of-birth" | "reported";

export type AgeBasis = "exact" | "approximate" | "reported" | "imported" | "unknown";

export type BirthdateMetadata = {
  birthdatePrecision?: BirthdatePrecision;
  birthdateOriginalValue?: string;
  birthYear?: number | null;
  birthMonth?: number | null;
  birthDay?: number | null;
  importedAge?: number | null;
  importedAgeAsOf?: string | null;
  birthdateSource?: BirthdateSource;
  dateOfBirthUnknown?: boolean;
  reportedAge?: number | null;
  ageAsOfDate?: string | null;
  ageSource?: AgeSource | null;
};

export type BirthdateResolverInput = BirthdateMetadata & {
  dateOfBirth?: string | Date | null;
  legacyOriginalBirthdate?: unknown;
  legacyBirthdateRaw?: unknown;
  legacyBirthdatePrecision?: unknown;
  legacyImportedAgeFallback?: unknown;
  legacyBirthYearRaw?: unknown;
  legacyAge?: unknown;
  legacyOriginalAge?: unknown;
  legacyComputedAge?: unknown;
  computedAge?: unknown;
  minimumAgeYears?: unknown;
  maximumAgeYears?: unknown;
};

export type ResolvedBirthdate = {
  precision: BirthdatePrecision;
  source: BirthdateSource | null;
  originalValue: string;
  compatibilityDate: string;
  year: number | null;
  month: number | null;
  day: number | null;
  displayBirthdate: string;
  ageYears: number | null;
  minimumAgeYears: number | null;
  maximumAgeYears: number | null;
  ageDisplay: string;
  ageBasis: AgeBasis;
  ageSource: AgeSource | null;
  ageAsOfDate: string | null;
  isApproximate: boolean;
};

export type ParsedBirthdateValue = {
  originalValue: string;
  parsedDate: Date | null;
  parsedYear: number | null;
  parsedMonth: number | null;
  parsedDay: number | null;
  isPartialDate: boolean;
  precision: BirthdatePrecision;
};

export function resolveBirthdate(
  input: BirthdateResolverInput,
  referenceDate?: Date,
): ResolvedBirthdate {
  const storedReferenceDate = parseMMDDYYYY(input.ageAsOfDate);
  const today = copyDateOnly(referenceDate ?? storedReferenceDate ?? getPhilippineCalendarDate());
  const shouldPreserveStoredSnapshot = !referenceDate && !storedReferenceDate;
  const dateOfBirthValue = normalizeResolverValue(input.dateOfBirth);
  const canonicalOriginalValue = normalizeResolverValue(input.birthdateOriginalValue);
  const legacyOriginalValue =
    normalizeResolverValue(input.legacyOriginalBirthdate) ||
    normalizeResolverValue(input.legacyBirthdateRaw);
  const originalValue = canonicalOriginalValue || legacyOriginalValue || dateOfBirthValue;
  const parsedOriginal = parseFlexibleBirthdateValue(originalValue, today);
  const parsedCompatibilityDate = parseMMDDYYYY(input.dateOfBirth);
  const canonicalPrecision = normalizeBirthdatePrecision(input.birthdatePrecision);
  const legacyPrecision = normalizeBirthdatePrecision(input.legacyBirthdatePrecision);
  const recoveredPrecision = isUsefulRecoveredPrecision(parsedOriginal.precision)
    ? parsedOriginal.precision
    : null;
  let precision =
    getAuthoritativePrecision(canonicalPrecision, legacyPrecision) ||
    recoveredPrecision ||
    (parsedCompatibilityDate ? "exact" : "unknown");
  const source =
    normalizeBirthdateSource(input.birthdateSource) ||
    (hasLegacyBirthdateEvidence(input, legacyOriginalValue, legacyPrecision)
      ? "legacy-import"
      : parsedCompatibilityDate
        ? "manual"
        : null);
  const importedAge = firstValidAge([
    input.importedAge,
    input.legacyImportedAgeFallback,
    input.legacyOriginalAge,
    input.legacyAge,
    input.legacyComputedAge,
    input.computedAge,
    parsedOriginal.precision === "age-only" ? parsedOriginal.originalValue : null,
  ]);
  const reportedAge = parseReportedAge(input.reportedAge);
  const isReportedAge = input.dateOfBirthUnknown === true || input.ageSource === "reported";
  const ageAsOfDate = normalizeAgeAsOfDate(input.ageAsOfDate);
  const authoritativeAgeAsOfDate = referenceDate ? formatToMMDDYYYY(today) : ageAsOfDate;
  const preservedSnapshotAge = shouldPreserveStoredSnapshot
    ? firstValidAge([input.computedAge, input.legacyComputedAge, input.legacyAge, input.legacyOriginalAge])
    : null;
  const preservedMinimumAge = shouldPreserveStoredSnapshot
    ? firstValidAge([input.minimumAgeYears, preservedSnapshotAge])
    : null;
  const preservedMaximumAge = shouldPreserveStoredSnapshot
    ? firstValidAge([input.maximumAgeYears, preservedSnapshotAge])
    : null;

  if (isReportedAge) {
    return {
      precision: "age-only",
      source: "manual",
      originalValue: "",
      compatibilityDate: "",
      year: null,
      month: null,
      day: null,
      displayBirthdate: "Unknown",
      ageYears: reportedAge,
      minimumAgeYears: reportedAge,
      maximumAgeYears: reportedAge,
      ageDisplay: formatResolvedAgeDisplay(reportedAge),
      ageBasis: reportedAge === null ? "unknown" : "reported",
      ageSource: "reported",
      ageAsOfDate: authoritativeAgeAsOfDate,
      isApproximate: false,
    };
  }

  if (precision === "unknown" && importedAge !== null && !parsedCompatibilityDate) {
    precision = "age-only";
  }

  const year =
    normalizeBirthYear(input.birthYear, today) ||
    normalizeBirthYear(parsedOriginal.parsedYear, today) ||
    normalizeBirthYear(input.legacyBirthYearRaw, today) ||
    parsedCompatibilityDate?.getFullYear() ||
    null;
  const month =
    normalizeBirthMonth(input.birthMonth) ||
    normalizeBirthMonth(parsedOriginal.parsedMonth) ||
    (precision !== "year-only" && precision !== "age-only"
      ? parsedCompatibilityDate
        ? parsedCompatibilityDate.getMonth() + 1
        : null
      : null);
  const day =
    precision === "exact"
      ? normalizeBirthDay(input.birthDay) ||
        normalizeBirthDay(parsedOriginal.precision === "exact" ? parsedOriginal.parsedDay : null) ||
        parsedCompatibilityDate?.getDate() ||
        null
      : null;

  if (precision === "exact" && !buildDateFromParts(year, month, day)) {
    precision = "unknown";
  } else if (precision === "month-year" && (!year || !month)) {
    precision = "unknown";
  } else if (precision === "year-only" && !year) {
    precision = "unknown";
  }

  if (precision === "exact") {
    const exactDate = buildDateFromParts(year, month, day);
    const todayDate = copyDateOnly(today);
    const ageDetails = exactDate && exactDate <= todayDate
      ? calculateExactAgeDetails(exactDate, copyDateOnly(today), precision)
      : null;
    const ageYears = preservedSnapshotAge ?? ageDetails?.completedYears ?? null;
    const compatibilityDate = exactDate ? formatToMMDDYYYY(exactDate) : dateOfBirthValue;

    return {
      precision,
      source,
      originalValue,
      compatibilityDate,
      year,
      month,
      day,
      displayBirthdate: compatibilityDate || "Not specified",
      ageYears,
      minimumAgeYears: ageYears,
      maximumAgeYears: ageYears,
      ageDisplay: formatResolvedAgeDisplay(ageYears),
      ageBasis: ageYears === null ? "unknown" : "exact",
      ageSource: ageYears === null ? null : "date-of-birth",
      ageAsOfDate: authoritativeAgeAsOfDate,
      isApproximate: false,
    };
  }

  if (precision === "month-year" && year && month) {
    const compatibilityDate =
      parsedCompatibilityDate
        ? formatToMMDDYYYY(parsedCompatibilityDate)
        : formatToMMDDYYYY(new Date(year, month - 1, 1));
    const approximateAge = preservedSnapshotAge ?? calculateAgeDetailsFromFlexibleBirthdate(
      buildParsedBirthdateValue({
        originalValue,
        parsedDate: new Date(year, month - 1, 1),
        parsedYear: year,
        parsedMonth: month,
        parsedDay: null,
        precision,
      }),
      today,
    )?.completedYears ?? null;
    const ageRange = preservedSnapshotAge === null
      ? calculateMonthYearAgeRange(year, month, today)
      : { minimum: preservedMinimumAge, maximum: preservedMaximumAge };

    return {
      precision,
      source,
      originalValue,
      compatibilityDate,
      year,
      month,
      day: null,
      displayBirthdate: formatMonthYearDisplay(year, month),
      ageYears: approximateAge,
      minimumAgeYears: ageRange.minimum,
      maximumAgeYears: ageRange.maximum,
      ageDisplay: formatResolvedAgeDisplay(approximateAge),
      ageBasis: "approximate",
      ageSource: null,
      ageAsOfDate: authoritativeAgeAsOfDate,
      isApproximate: true,
    };
  }

  if (precision === "year-only" && year) {
    const compatibilityDate =
      parsedCompatibilityDate
        ? formatToMMDDYYYY(parsedCompatibilityDate)
        : formatToMMDDYYYY(new Date(year, 0, 1));
    const approximateAge = preservedSnapshotAge ?? calculateAgeDetailsFromFlexibleBirthdate(
      buildParsedBirthdateValue({
        originalValue,
        parsedDate: new Date(year, 0, 1),
        parsedYear: year,
        parsedMonth: null,
        parsedDay: null,
        precision,
      }),
      today,
    )?.completedYears ?? null;
    const ageRange = preservedSnapshotAge === null
      ? calculateYearOnlyAgeRange(year, today)
      : { minimum: preservedMinimumAge, maximum: preservedMaximumAge };

    return {
      precision,
      source,
      originalValue,
      compatibilityDate,
      year,
      month: null,
      day: null,
      displayBirthdate: String(year),
      ageYears: approximateAge,
      minimumAgeYears: ageRange.minimum,
      maximumAgeYears: ageRange.maximum,
      ageDisplay: formatResolvedAgeDisplay(approximateAge),
      ageBasis: "approximate",
      ageSource: null,
      ageAsOfDate: authoritativeAgeAsOfDate,
      isApproximate: true,
    };
  }

  if (precision === "age-only") {
    return {
      precision,
      source,
      originalValue,
      compatibilityDate: "",
      year: null,
      month: null,
      day: null,
      displayBirthdate: "Birthdate unknown",
      ageYears: importedAge,
      minimumAgeYears: importedAge,
      maximumAgeYears: importedAge,
      ageDisplay: formatResolvedAgeDisplay(importedAge),
      ageBasis: importedAge === null ? "unknown" : "imported",
      ageSource: null,
      ageAsOfDate: normalizeAgeAsOfDate(input.importedAgeAsOf),
      isApproximate: importedAge !== null,
    };
  }

  return {
    precision: "unknown",
    source,
    originalValue,
    compatibilityDate: "",
    year: null,
    month: null,
    day: null,
    displayBirthdate: "Not specified",
    ageYears: null,
    minimumAgeYears: null,
    maximumAgeYears: null,
    ageDisplay: "",
    ageBasis: "unknown",
    ageSource: null,
    ageAsOfDate,
    isApproximate: false,
  };
}

export type AgeReferenceInterview = {
  dateOfVisit?: string | null;
  result?: string | null;
};

export type AgeReferenceMember = {
  ageAsOfDate?: string | null;
  birthdateSource?: BirthdateSource | null;
};

export type AgeReferenceOptions = {
  members?: readonly AgeReferenceMember[] | null;
  fallbackDate?: Date;
};

export function getPhilippineCalendarDate(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  return new Date(Number(values.year), Number(values.month) - 1, Number(values.day));
}

export function resolveAgeReferenceDate(
  interviews: readonly AgeReferenceInterview[] | null | undefined,
  options: AgeReferenceOptions | Date = {},
) {
  const normalizedOptions = options instanceof Date ? { fallbackDate: options } : options;
  const fallback = copyDateOnly(normalizedOptions.fallbackDate ?? getPhilippineCalendarDate());
  const completedVisitDate = resolveSuccessfulInterviewDate(interviews, fallback);

  if (completedVisitDate) {
    return completedVisitDate;
  }

  for (const member of normalizedOptions.members ?? []) {
    const persistedReferenceDate = parseMMDDYYYY(member.ageAsOfDate);

    if (persistedReferenceDate && persistedReferenceDate <= fallback) {
      return persistedReferenceDate;
    }
  }

  return fallback;
}

export function resolveSuccessfulInterviewDate(
  interviews: readonly AgeReferenceInterview[] | null | undefined,
  maximumDate = getPhilippineCalendarDate(),
) {
  const maximum = copyDateOnly(maximumDate);
  const completedVisitDates = (interviews ?? [])
    .filter((interview) => isCompletedInterviewResult(interview.result))
    .map((interview) => parseMMDDYYYY(interview.dateOfVisit))
    .filter((date): date is Date => Boolean(date && date <= maximum));

  if (completedVisitDates.length > 0) {
    return completedVisitDates.reduce((latest, candidate) => candidate > latest ? candidate : latest);
  }

  return null;
}

export function isCompletedInterviewResult(value: unknown) {
  return String(value ?? "").trim().toUpperCase() === "C";
}

export function getAgeReferenceDateValue(
  interviews: readonly AgeReferenceInterview[] | null | undefined,
  options: AgeReferenceOptions | Date = {},
) {
  return formatToMMDDYYYY(resolveAgeReferenceDate(interviews, options));
}

export function resolveMemberAge(
  input: BirthdateResolverInput,
  referenceDate?: Date,
) {
  return resolveBirthdate(input, referenceDate);
}

export function parseReportedAge(value: unknown) {
  if (typeof value === "number") {
    return Number.isInteger(value) && value >= 0 && value <= 120 ? value : null;
  }

  const normalized = String(value ?? "").trim();

  if (!/^\d{1,3}$/.test(normalized)) {
    return null;
  }

  const age = Number(normalized);
  return Number.isInteger(age) && age >= 0 && age <= 120 ? age : null;
}

export type AgeEntryModeValues = {
  dateOfBirthUnknown: boolean;
  dateOfBirth?: string;
  birthdateOriginalValue?: string;
  birthYear?: number | null;
  birthMonth?: number | null;
  birthDay?: number | null;
  birthdatePrecision: BirthdatePrecision;
  birthdateSource: BirthdateSource;
  reportedAge?: number | null;
  ageAsOfDate: string | null;
  ageSource: AgeSource | null;
};

export function getAgeEntryModeValues(
  dateOfBirthUnknown: boolean,
  ageReferenceDate: string,
): AgeEntryModeValues {
  if (dateOfBirthUnknown) {
    return {
      dateOfBirthUnknown: true,
      dateOfBirth: "",
      birthdateOriginalValue: "",
      birthYear: null,
      birthMonth: null,
      birthDay: null,
      birthdatePrecision: "age-only",
      birthdateSource: "manual",
      ageAsOfDate: ageReferenceDate,
      ageSource: "reported",
    };
  }

  return {
    dateOfBirthUnknown: false,
    birthdatePrecision: "unknown",
    birthdateSource: "manual",
    reportedAge: null,
    ageAsOfDate: ageReferenceDate,
    ageSource: null,
  };
}

export type MemberAgeSourceValidationResult = {
  isValid: boolean;
  field?: "dateOfBirth" | "reportedAge";
  message?: string;
};

export function validateMemberAgeSource(
  input: BirthdateResolverInput,
  referenceDate = getPhilippineCalendarDate(),
): MemberAgeSourceValidationResult {
  const dateOfBirth = normalizeResolverValue(input.dateOfBirth);
  const reportedAge = parseReportedAge(input.reportedAge);
  const rawReportedAge = String(input.reportedAge ?? "").trim();

  if (input.dateOfBirthUnknown === true) {
    if (dateOfBirth || input.ageSource === "date-of-birth") {
      return {
        isValid: false,
        field: "dateOfBirth",
        message: "Date of Birth must be blank when Date of Birth Unknown is selected.",
      };
    }

    if (!rawReportedAge) {
      return {
        isValid: false,
        field: "reportedAge",
        message: "Enter the person’s reported age.",
      };
    }

    if (reportedAge === null) {
      return {
        isValid: false,
        field: "reportedAge",
        message: "Age must be a whole number from 0 to 120.",
      };
    }

    return { isValid: true };
  }

  if (rawReportedAge || input.ageSource === "reported") {
    return {
      isValid: false,
      field: "reportedAge",
      message: "Clear the reported age or select Date of Birth Unknown.",
    };
  }

  if (!dateOfBirth) {
    return {
      isValid: false,
      field: "dateOfBirth",
      message: "Enter the Date of Birth or select Date of Birth Unknown.",
    };
  }

  const dobValidation = validateDOB(dateOfBirth, referenceDate);
  return dobValidation.isValid
    ? { isValid: true }
    : {
        isValid: false,
        field: "dateOfBirth",
        message: dobValidation.message ?? birthdateValidationMessage,
      };
}

export function formatAgeAsOfDate(value: string | null | undefined) {
  const date = parseMMDDYYYY(value);

  if (!date) {
    return "";
  }

  return `${englishMonthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export function getAgeSourceLabel(resolved: ResolvedBirthdate) {
  if (resolved.ageBasis === "reported") {
    const asOfDate = formatAgeAsOfDate(resolved.ageAsOfDate);
    return asOfDate ? `Reported as of ${asOfDate}.` : "Reported age.";
  }

  if (resolved.ageBasis === "imported") {
    return "Imported age.";
  }

  if (resolved.ageBasis === "exact") {
    return "Automatically calculated from Date of Birth.";
  }

  return "";
}

export function formatOfficialAgeDisplay(resolved: ResolvedBirthdate) {
  if (!resolved.ageDisplay) {
    return "";
  }

  if (resolved.ageBasis === "reported") {
    const asOfDate = formatAgeAsOfDate(resolved.ageAsOfDate);
    return asOfDate
      ? `${resolved.ageDisplay} — Reported as of ${asOfDate}.`
      : `${resolved.ageDisplay} — Reported`;
  }

  if (resolved.ageBasis === "imported") {
    return `Imported age: ${resolved.ageDisplay}`;
  }

  return resolved.ageDisplay;
}

export function getOfficialAgeGroup(ageYears: number | null) {
  if (ageYears === null || !Number.isFinite(ageYears)) {
    return "";
  }

  if (ageYears < 1) {
    return "Below 1";
  }

  if (ageYears <= 4) {
    return "1-4";
  }

  if (ageYears <= 14) {
    return "5-14";
  }

  if (ageYears <= 24) {
    return "15-24";
  }

  if (ageYears <= 59) {
    return "25-59";
  }

  return "60 and above";
}

const englishMonthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export type BirthdateAgeDetails = {
  displayAge: string;
  completedYears: number;
  years: number;
  months: number;
  monthsOld: number | null;
  totalCompletedMonths: number;
  ageInYearsFraction: number;
  isApproximate?: boolean;
  precision?: BirthdatePrecision;
};

export function calculateAgeDetailsFromBirthdate(
  birthdate: string | Date | null | undefined,
  today = new Date(),
): BirthdateAgeDetails | null {
  return calculateAgeDetailsFromFlexibleBirthdate(parseFlexibleBirthdateValue(birthdate, today), today);
}

export function calculateAgeFromParsedBirthdate(parsedValue: ParsedBirthdateValue, today = new Date()) {
  if (parsedValue.precision === "age-only") {
    return parseAgeOnlyValue(parsedValue.originalValue);
  }

  return calculateAgeDetailsFromFlexibleBirthdate(parsedValue, today)?.completedYears ?? null;
}

export function calculateAgeDetailsFromFlexibleBirthdate(parsedValue: ParsedBirthdateValue, today = new Date()) {
  if (parsedValue.precision === "unknown" || parsedValue.precision === "age-only") {
    return null;
  }

  if (!parsedValue.parsedDate) {
    return null;
  }

  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  if (parsedValue.parsedDate > todayDate) {
    return null;
  }

  if (parsedValue.precision === "year-only" && parsedValue.parsedYear) {
    const completedYears = Math.max(0, todayDate.getFullYear() - parsedValue.parsedYear);

    return {
      completedYears,
      years: completedYears,
      months: 0,
      displayAge: `${completedYears} ${completedYears === 1 ? "year" : "years"}`,
      monthsOld: null,
      totalCompletedMonths: completedYears * 12,
      ageInYearsFraction: completedYears,
      isApproximate: true,
      precision: parsedValue.precision,
    };
  }

  if (parsedValue.precision === "month-year" && parsedValue.parsedYear && parsedValue.parsedMonth) {
    const birthMonthIndex = parsedValue.parsedMonth - 1;
    const monthHasPassedOrIsCurrent = todayDate.getMonth() >= birthMonthIndex;
    const completedYears = Math.max(
      0,
      todayDate.getFullYear() - parsedValue.parsedYear - (monthHasPassedOrIsCurrent ? 0 : 1),
    );

    return {
      completedYears,
      years: completedYears,
      months: 0,
      displayAge: `${completedYears} ${completedYears === 1 ? "year" : "years"}`,
      monthsOld: null,
      totalCompletedMonths: completedYears * 12,
      ageInYearsFraction: completedYears,
      isApproximate: true,
      precision: parsedValue.precision,
    };
  }

  return calculateExactAgeDetails(parsedValue.parsedDate, todayDate, parsedValue.precision);
}

export function getBirthdatePrecisionLabel(precision: BirthdatePrecision) {
  const labels: Record<BirthdatePrecision, string> = {
    exact: "Exact date",
    "month-year": "Month and year only",
    "year-only": "Year only",
    "age-only": "Age only",
    unknown: "Unknown",
  };

  return labels[precision];
}

export function calculateAgeDisplayFromBirthdate(birthdate: string | Date | null | undefined, today = new Date()) {
  return calculateAgeDetailsFromBirthdate(birthdate, today)?.displayAge ?? null;
}

export const calculateAgeFromDOB = calculateAgeDetailsFromBirthdate;

export const birthdateValidationMessage =
  "Enter a valid Date of Birth.";

export const futureBirthdateValidationMessage =
  "Date of Birth cannot be in the future.";

export const requiredBirthdateValidationMessage =
  "Date of birth is required.";

const strictBirthdateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

export type DOBValidationResult = {
  isValid: boolean;
  message?: string;
  ageDetails?: BirthdateAgeDetails;
};

export function validateDOB(value: string | Date | null | undefined, today = new Date()): DOBValidationResult {
  const trimmedValue = normalizeExternalDOBValue(value);

  if (!trimmedValue) {
    return {
      isValid: false,
      message: requiredBirthdateValidationMessage,
    };
  }

  if (trimmedValue.replace(/\D/g, "").length < 8) {
    return {
      isValid: false,
      message: birthdateValidationMessage,
    };
  }

  if (!strictBirthdateRegex.test(trimmedValue)) {
    return {
      isValid: false,
      message: birthdateValidationMessage,
    };
  }

  const parsedBirthdate = parseMMDDYYYY(trimmedValue);
  const currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  if (!parsedBirthdate || parsedBirthdate.getFullYear() < 1900) {
    return {
      isValid: false,
      message: birthdateValidationMessage,
    };
  }

  if (parsedBirthdate > currentDate) {
    return {
      isValid: false,
      message: futureBirthdateValidationMessage,
    };
  }

  const ageDetails = calculateAgeDetailsFromBirthdate(trimmedValue, today);

  if (!ageDetails || ageDetails.completedYears < 0) {
    return {
      isValid: false,
      message: birthdateValidationMessage,
    };
  }

  return {
    isValid: true,
    ageDetails,
  };
}

export function normalizeDOBInput(value: string | Date | null | undefined) {
  if (value instanceof Date) {
    return formatToMMDDYYYY(value);
  }

  const normalizedValue = String(value ?? "").replace(/-/g, "/");
  const digits = normalizedValue.replace(/\D/g, "").slice(0, 8);
  const endsWithSeparator = /\/$/.test(normalizedValue);

  if (digits.length <= 2) {
    return endsWithSeparator && digits.length === 2 ? `${digits}/` : digits;
  }

  if (digits.length <= 4) {
    const monthDayValue = `${digits.slice(0, 2)}/${digits.slice(2)}`;

    return endsWithSeparator && digits.length === 4 ? `${monthDayValue}/` : monthDayValue;
  }

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export const formatBirthdateInput = normalizeDOBInput;

export function isValidDOB(value: string | Date | null | undefined, today = new Date()) {
  return validateDOB(value, today).isValid;
}

export function isValidBirthdate(value: string | Date | null | undefined, today = new Date()) {
  const parsedDate = parseMMDDYYYY(value);

  if (!isRealBirthdate(value) || !parsedDate) {
    return false;
  }

  const currentYear = today.getFullYear();
  const todayDate = new Date(currentYear, today.getMonth(), today.getDate());

  return parsedDate.getFullYear() <= currentYear && parsedDate <= todayDate;
}

export function isFutureDate(value: string | Date | null | undefined, today = new Date()) {
  const parsedDate = parseMMDDYYYY(value);

  if (!parsedDate) {
    return false;
  }

  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  return parsedDate > todayDate;
}

export function isRealBirthdate(value: string | Date | null | undefined) {
  const parsedDate = parseMMDDYYYY(value);

  return Boolean(parsedDate && parsedDate.getFullYear() >= 1900);
}

export function isRecognizedBirthdate(value: string | Date | null | undefined) {
  return isValidBirthdate(value);
}

export function cleanBirthdateValue(value: string | Date | null | undefined) {
  const parsedValue = parseFlexibleBirthdateValue(value);
  const parsedDate = parsedValue.parsedDate;

  if (!parsedDate) {
    if (value instanceof Date) {
      return "";
    }

    return String(value ?? "").replace(/\s+/g, " ").trim();
  }

  return formatToMMDDYYYY(parsedDate);
}

export function normalizeBirthdateForStorage(value: string) {
  return cleanBirthdateValue(value);
}

export function formatParsedBirthdateForStorage(parsedValue: ParsedBirthdateValue) {
  if (!parsedValue.parsedDate || parsedValue.precision === "age-only" || parsedValue.precision === "unknown") {
    return "";
  }

  return formatToMMDDYYYY(parsedValue.parsedDate);
}

export function parseMMDDYYYY(input: string | Date | null | undefined): Date | null {
  if (input instanceof Date) {
    return isValidDateObject(input) ? copyDateOnly(input) : null;
  }

  const trimmedValue = String(input ?? "").trim();
  const monthFirstMatch = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(trimmedValue);

  if (monthFirstMatch) {
    return parseValidDate(monthFirstMatch[3], monthFirstMatch[1], monthFirstMatch[2]);
  }

  return null;
}

export function parseFlexibleBirthdateValue(
  input: string | Date | number | null | undefined,
  today = new Date(),
): ParsedBirthdateValue {
  if (input instanceof Date) {
    const parsedDate = isValidDateObject(input) ? copyDateOnly(input) : null;

    return buildParsedBirthdateValue({
      originalValue: parsedDate ? formatToMMDDYYYY(parsedDate) : "",
      parsedDate,
      precision: parsedDate ? "exact" : "unknown",
    });
  }

  const originalValue = String(input ?? "").replace(/\s+/g, " ").trim();

  if (!originalValue || /^null$/i.test(originalValue) || /^undefined$/i.test(originalValue)) {
    return buildParsedBirthdateValue({ originalValue, precision: "unknown" });
  }

  const currentYear = today.getFullYear();
  const excelSerial = Number(originalValue);

  if (/^\d{4,6}(?:\.\d+)?$/.test(originalValue) && Number.isFinite(excelSerial) && excelSerial > 20000 && excelSerial < 80000) {
    const parsedDate = new Date(Math.round((excelSerial - 25569) * 86400 * 1000));

    return buildParsedBirthdateValue({
      originalValue,
      parsedDate,
      precision: "exact",
    });
  }

  const exactDate = parseExactFlexibleDate(originalValue);

  if (exactDate) {
    return buildParsedBirthdateValue({
      originalValue,
      parsedDate: exactDate,
      precision: "exact",
    });
  }

  const monthYear = parseMonthYearValue(originalValue, currentYear);

  if (monthYear) {
    return buildParsedBirthdateValue({
      originalValue,
      parsedDate: new Date(monthYear.year, monthYear.month - 1, 1),
      parsedYear: monthYear.year,
      parsedMonth: monthYear.month,
      parsedDay: null,
      precision: "month-year",
    });
  }

  const yearOnly = /^(\d{4})$/.exec(originalValue);

  if (yearOnly) {
    const parsedYear = Number(yearOnly[1]);

    if (parsedYear >= 1900 && parsedYear <= currentYear) {
      return buildParsedBirthdateValue({
        originalValue,
        parsedDate: new Date(parsedYear, 0, 1),
        parsedYear,
        parsedMonth: null,
        parsedDay: null,
        precision: "year-only",
      });
    }
  }

  const ageOnly = parseAgeOnlyValue(originalValue);

  if (ageOnly !== null) {
    return buildParsedBirthdateValue({
      originalValue,
      precision: "age-only",
    });
  }

  return buildParsedBirthdateValue({ originalValue, precision: "unknown" });
}

export function formatToMMDDYYYY(date: Date | null | undefined) {
  if (!isValidDateObject(date)) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${month}/${day}/${year}`;
}

export function toCalendarDate(input: string | Date | null | undefined) {
  return parseMMDDYYYY(input);
}

function parseValidDate(yearValue: string, monthValue: string, dayValue: string) {
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);

  if (month < 1 || month > 12) {
    return null;
  }

  const parsedDate = new Date(year, month - 1, day);

  if (
    parsedDate.getFullYear() !== year ||
    parsedDate.getMonth() !== month - 1 ||
    parsedDate.getDate() !== day
  ) {
    return null;
  }

  return parsedDate;
}

const monthNameLookup: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

function buildParsedBirthdateValue({
  originalValue,
  parsedDate = null,
  parsedYear,
  parsedMonth,
  parsedDay,
  precision,
}: {
  originalValue: string;
  parsedDate?: Date | null;
  parsedYear?: number | null;
  parsedMonth?: number | null;
  parsedDay?: number | null;
  precision: BirthdatePrecision;
}): ParsedBirthdateValue {
  const dateOnly = parsedDate && isValidDateObject(parsedDate) ? copyDateOnly(parsedDate) : null;

  return {
    originalValue,
    parsedDate: dateOnly,
    parsedYear: parsedYear !== undefined ? parsedYear : (dateOnly ? dateOnly.getFullYear() : null),
    parsedMonth: parsedMonth !== undefined ? parsedMonth : (dateOnly ? dateOnly.getMonth() + 1 : null),
    parsedDay: parsedDay !== undefined ? parsedDay : (dateOnly ? dateOnly.getDate() : null),
    isPartialDate: precision === "month-year" || precision === "year-only" || precision === "age-only",
    precision,
  };
}

function parseAgeOnlyValue(value: string) {
  if (!/^\d{1,3}$/.test(value)) {
    return null;
  }

  const age = Number(value);

  return Number.isFinite(age) && age >= 0 && age <= 120 ? age : null;
}

function parseExactFlexibleDate(value: string) {
  const yearFirst = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/.exec(value);

  if (yearFirst) {
    return parseValidDate(yearFirst[1], yearFirst[2], yearFirst[3]);
  }

  const monthOrDayFirst = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(value);

  if (!monthOrDayFirst) {
    return null;
  }

  const first = Number(monthOrDayFirst[1]);
  const second = Number(monthOrDayFirst[2]);
  const month = first > 12 && second <= 12 ? second : first;
  const day = first > 12 && second <= 12 ? first : second;

  return parseValidDate(monthOrDayFirst[3], String(month), String(day));
}

function parseMonthYearValue(value: string, currentYear: number) {
  const namedMonthFirst = /^([A-Za-z]{3,9})[\s./-]+(\d{4})$/.exec(value);

  if (namedMonthFirst) {
    return buildMonthYearParseResult(namedMonthFirst[2], getMonthNameNumber(namedMonthFirst[1]), currentYear);
  }

  const namedMonthSecond = /^(\d{4})[\s./-]+([A-Za-z]{3,9})$/.exec(value);

  if (namedMonthSecond) {
    return buildMonthYearParseResult(namedMonthSecond[1], getMonthNameNumber(namedMonthSecond[2]), currentYear);
  }

  const numericMonthFirst = /^(\d{1,2})[/-](\d{4})$/.exec(value);

  if (numericMonthFirst) {
    return buildMonthYearParseResult(numericMonthFirst[2], Number(numericMonthFirst[1]), currentYear);
  }

  const numericYearFirst = /^(\d{4})[/-](\d{1,2})$/.exec(value);

  if (numericYearFirst) {
    return buildMonthYearParseResult(numericYearFirst[1], Number(numericYearFirst[2]), currentYear);
  }

  return null;
}

function buildMonthYearParseResult(yearValue: string, month: number | null, currentYear: number) {
  const year = Number(yearValue);

  if (!month || month < 1 || month > 12 || year < 1900 || year > currentYear) {
    return null;
  }

  return { year, month };
}

function getMonthNameNumber(value: string) {
  return monthNameLookup[value.toLowerCase()] ?? null;
}

function calculateExactAgeDetails(
  parsedBirthdate: Date,
  todayDate: Date,
  precision: BirthdatePrecision,
): BirthdateAgeDetails | null {
  const totalCompletedMonths = calculateCompletedMonths(parsedBirthdate, todayDate);
  const completedYears = Math.floor(totalCompletedMonths / 12);
  const remainingMonths = totalCompletedMonths % 12;
  const ageInYearsFraction = Number((totalCompletedMonths / 12).toFixed(2));

  if (totalCompletedMonths < 12) {
    const monthLabel = totalCompletedMonths === 1 ? "month" : "months";

    return {
      completedYears,
      years: completedYears,
      months: totalCompletedMonths,
      displayAge: `${totalCompletedMonths} ${monthLabel}`,
      monthsOld: totalCompletedMonths,
      totalCompletedMonths,
      ageInYearsFraction,
      isApproximate: false,
      precision,
    };
  }

  const yearLabel = completedYears === 1 ? "year" : "years";
  const monthLabel = remainingMonths === 1 ? "month" : "months";

  return {
    completedYears,
    years: completedYears,
    months: remainingMonths,
    displayAge:
      remainingMonths > 0
        ? `${completedYears} ${yearLabel}, ${remainingMonths} ${monthLabel}`
        : `${completedYears} ${yearLabel}`,
    monthsOld: null,
    totalCompletedMonths,
    ageInYearsFraction,
    isApproximate: false,
    precision,
  };
}

function normalizeResolverValue(value: unknown) {
  if (value instanceof Date) {
    return isValidDateObject(value) ? formatToMMDDYYYY(value) : "";
  }

  const normalized = String(value ?? "").replace(/\s+/g, " ").trim();

  return /^(null|undefined|none|n\/a|na|99(?:\s*-\s*n\/a)?)$/i.test(normalized)
    ? ""
    : normalized;
}

function normalizeBirthdatePrecision(value: unknown): BirthdatePrecision | null {
  const normalized = normalizeResolverValue(value).toLowerCase();

  return normalized === "exact" ||
    normalized === "month-year" ||
    normalized === "year-only" ||
    normalized === "age-only" ||
    normalized === "unknown"
    ? normalized
    : null;
}

function normalizeBirthdateSource(value: unknown): BirthdateSource | null {
  const normalized = normalizeResolverValue(value).toLowerCase();

  return normalized === "manual" || normalized === "legacy-import"
    ? normalized
    : null;
}

function normalizeAgeAsOfDate(value: unknown) {
  const parsedDate = parseMMDDYYYY(normalizeResolverValue(value));
  return parsedDate ? formatToMMDDYYYY(parsedDate) : null;
}

function getAuthoritativePrecision(
  canonicalPrecision: BirthdatePrecision | null,
  legacyPrecision: BirthdatePrecision | null,
) {
  if (canonicalPrecision && canonicalPrecision !== "unknown") {
    return canonicalPrecision;
  }

  if (legacyPrecision && legacyPrecision !== "unknown") {
    return legacyPrecision;
  }

  return null;
}

function isUsefulRecoveredPrecision(precision: BirthdatePrecision) {
  return precision !== "unknown";
}

function hasLegacyBirthdateEvidence(
  input: BirthdateResolverInput,
  legacyOriginalValue: string,
  legacyPrecision: BirthdatePrecision | null,
) {
  return Boolean(
    legacyOriginalValue ||
    legacyPrecision ||
    normalizeResolverValue(input.legacyImportedAgeFallback) ||
    normalizeResolverValue(input.legacyBirthYearRaw) ||
    normalizeResolverValue(input.legacyAge) ||
    normalizeResolverValue(input.legacyOriginalAge) ||
    normalizeResolverValue(input.legacyComputedAge),
  );
}

function normalizeBirthYear(value: unknown, today: Date) {
  const normalized = normalizeResolverValue(value);

  if (!/^\d{4}$/.test(normalized)) {
    return null;
  }

  const year = Number(normalized);

  return year >= 1900 && year <= today.getFullYear() ? year : null;
}

function normalizeBirthMonth(value: unknown) {
  const normalized = normalizeResolverValue(value);

  if (!/^\d{1,2}$/.test(normalized)) {
    return null;
  }

  const month = Number(normalized);

  return month >= 1 && month <= 12 ? month : null;
}

function normalizeBirthDay(value: unknown) {
  const normalized = normalizeResolverValue(value);

  if (!/^\d{1,2}$/.test(normalized)) {
    return null;
  }

  const day = Number(normalized);

  return day >= 1 && day <= 31 ? day : null;
}

function firstValidAge(values: unknown[]) {
  for (const value of values) {
    const normalized = normalizeResolverValue(value);
    const age = parseAgeOnlyValue(normalized);

    if (age !== null) {
      return age;
    }
  }

  return null;
}

function buildDateFromParts(
  year: number | null,
  month: number | null,
  day: number | null,
) {
  if (!year || !month || !day) {
    return null;
  }

  return parseValidDate(String(year), String(month), String(day));
}

function formatMonthYearDisplay(year: number, month: number) {
  return new Intl.DateTimeFormat("en", {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, 1));
}

function formatResolvedAgeDisplay(age: number | null) {
  if (age === null) {
    return "";
  }

  return `${age} ${age === 1 ? "year" : "years"}`;
}

function calculateMonthYearAgeRange(year: number, month: number, today: Date) {
  const todayDate = copyDateOnly(today);
  const earliestPossibleDate = new Date(year, month - 1, 1);

  if (earliestPossibleDate > todayDate) {
    return { minimum: null, maximum: null };
  }

  const latestPossibleDate = new Date(year, month, 0);
  const maximum = calculatePossibleCompletedYears(earliestPossibleDate, todayDate);
  const minimum = latestPossibleDate > todayDate
    ? 0
    : calculatePossibleCompletedYears(latestPossibleDate, todayDate);

  return { minimum, maximum };
}

function calculateYearOnlyAgeRange(year: number, today: Date) {
  const todayDate = copyDateOnly(today);
  const earliestPossibleDate = new Date(year, 0, 1);

  if (earliestPossibleDate > todayDate) {
    return { minimum: null, maximum: null };
  }

  const latestPossibleDate = new Date(year, 11, 31);
  const maximum = calculatePossibleCompletedYears(earliestPossibleDate, todayDate);
  const minimum = latestPossibleDate > todayDate
    ? 0
    : calculatePossibleCompletedYears(latestPossibleDate, todayDate);

  return { minimum, maximum };
}

function calculatePossibleCompletedYears(birthdate: Date, today: Date) {
  return calculateExactAgeDetails(birthdate, today, "exact")?.completedYears ?? null;
}

function normalizeExternalDOBValue(value: string | Date | null | undefined) {
  if (value instanceof Date) {
    return formatToMMDDYYYY(value);
  }

  return String(value ?? "").trim();
}

function isValidDateObject(value: Date | null | undefined): value is Date {
  return value instanceof Date && Number.isFinite(value.getTime());
}

function copyDateOnly(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function calculateCompletedMonths(birthdate: Date, today: Date) {
  let months =
    (today.getFullYear() - birthdate.getFullYear()) * 12 +
    (today.getMonth() - birthdate.getMonth());

  if (today.getDate() < birthdate.getDate()) {
    months -= 1;
  }

  return Math.max(months, 0);
}
